/**
 * The single source of truth for P.H.I.S.H. pillar theming.
 *
 * Before this, three files (phish-mnemonic-strip, pillar-mastery-strip,
 * framework-reference) each hardcoded their own copy of the pillar colour map
 * and they had already drifted (PF3 purple-500 vs #a855f7). Import from here
 * everywhere a pillar is coloured so the five pillars read identically across
 * the home hero, /play/me, the framework page, and the attribution drill-down.
 */
import type { PhishPillarCode } from './types'

export interface PillarTheme {
  /** Raw hex — for inline styles, canvas/chart strokes, ring SVGs. */
  hex: string
  /** Solid fill for the letter medallion + the mastery meter. */
  fill: string
  /** Border colour for the card's top accent hairline. */
  border: string
  /** Subtle tinted surface (hero wash, hover). */
  wash: string
  /** focus-visible / active ring colour. */
  ring: string
  /** Text colour that reads on the app surface in both themes. */
  text: string
}

export const PILLAR_THEME: Record<PhishPillarCode, PillarTheme> = {
  PF1: { hex: '#ef4444', fill: 'bg-red-500', border: 'border-red-500', wash: 'bg-red-500/5', ring: 'ring-red-500/50', text: 'text-red-600 dark:text-red-400' },
  PF2: { hex: '#f59e0b', fill: 'bg-amber-500', border: 'border-amber-500', wash: 'bg-amber-500/5', ring: 'ring-amber-500/50', text: 'text-amber-600 dark:text-amber-400' },
  PF3: { hex: '#a855f7', fill: 'bg-purple-500', border: 'border-purple-500', wash: 'bg-purple-500/5', ring: 'ring-purple-500/50', text: 'text-purple-600 dark:text-purple-400' },
  PF4: { hex: '#3b82f6', fill: 'bg-blue-500', border: 'border-blue-500', wash: 'bg-blue-500/5', ring: 'ring-blue-500/50', text: 'text-blue-600 dark:text-blue-400' },
  PF5: { hex: '#10b981', fill: 'bg-emerald-500', border: 'border-emerald-500', wash: 'bg-emerald-500/5', ring: 'ring-emerald-500/50', text: 'text-emerald-600 dark:text-emerald-400' },
}

export const pillarTheme = (code: PhishPillarCode): PillarTheme =>
  PILLAR_THEME[code] ?? PILLAR_THEME.PF1
